import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Subscription, switchMap } from 'rxjs';
import {
  Anexo,
  WorkflowDetalhe,
  WorkflowEtapa,
  WorkflowEtapaTipo,
} from '../../core/models/workflow.models';
import { WorkflowsService } from '../../core/services/workflows.service';
import { CommunicationListComponent } from '../../shared/components/communication-list.component';
import { HistoryTableComponent } from '../../shared/components/history-table.component';
import { UploadListComponent } from '../../shared/components/upload-list.component';

const WORKFLOW_STEP_DEFINITIONS: Array<{ tipo: WorkflowEtapaTipo; label: string }> = [
  { tipo: WorkflowEtapaTipo.ABERTURA_OS, label: 'Abertura de OS' },
  { tipo: WorkflowEtapaTipo.COMPRA_PECAS, label: 'Compra de Peças' },
  { tipo: WorkflowEtapaTipo.PROGRAMACAO_REPARO, label: 'Programação do Reparo' },
  { tipo: WorkflowEtapaTipo.DESMONTAGEM, label: 'Desmontagem' },
  { tipo: WorkflowEtapaTipo.ANALISE_COMPLEMENTO, label: 'Análise de Complemento' },
  { tipo: WorkflowEtapaTipo.FUNILARIA, label: 'Funilaria' },
  { tipo: WorkflowEtapaTipo.PREPARACAO, label: 'Preparação' },
  { tipo: WorkflowEtapaTipo.PINTURA, label: 'Pintura' },
  { tipo: WorkflowEtapaTipo.MECANICA, label: 'Mecânica' },
  { tipo: WorkflowEtapaTipo.MONTAGEM, label: 'Montagem' },
  { tipo: WorkflowEtapaTipo.POLIMENTO, label: 'Polimento' },
  { tipo: WorkflowEtapaTipo.LAVAGEM, label: 'Lavagem / Higienização' },
  { tipo: WorkflowEtapaTipo.ACABAMENTO, label: 'Acabamento' },
  { tipo: WorkflowEtapaTipo.CONTROLE_QUALIDADE, label: 'Controle Qualidade Final' },
  { tipo: WorkflowEtapaTipo.VISTORIA_SEGURADORA, label: 'Fotos / Vistoria Seguradora' },
  { tipo: WorkflowEtapaTipo.FINALIZADO, label: 'Finalizado' },
];

const CHECKLIST_POR_ETAPA: Record<string, string[]> = {
  'Abertura de OS': ['Conferir relato do sinistro'],
  'Compra de Peças': [
    'Gerar lista de peças necessárias',
    'Solicitar cotação ao fornecedor',
    'Aprovar orçamento de peças',
    'Realizar pedido',
    'Recebimento e conferência',
  ],
  'Programação do Reparo': ['Conferir e programar produção conforme data de entrega'],
  Desmontagem: [
    'Conferir peças Orçamento',
    'Armazenar peças removidas e não substitu—das em local adequado e identificá-las',
    'Fazer complemento Desmontagem',
    'Verificar e anotar o complemento de Mecânica e Funilaria',
  ],
  'Análise de Complemento': [
    'Complemento Desmontagem',
    'Complemento Mecânica',
    'Complemento Funilaria',
  ],
  Funilaria: [
    'Conferir peças Orçamento',
    'Armazenar peças em local adequado',
    'Passou pelo Controle Qualidade?',
  ],
  Preparação: [
    'Funilaria está OK?',
    'Conferir peças Orçamento',
    'Peças removidas: tirar foto antes de sucatear',
    'Passou pelo Controle Qualidade?',
  ],
  Pintura: ['Preparação está OK?', 'Conferir peças Orçamento', 'Passou pelo Controle Qualidade?'],
  Mecânica: [
    'Pintura está OK?',
    'Conferir peças Orçamento',
    'Peças removidas: tirar foto antes de sucatear',
    'Passou pelo Controle Qualidade?',
  ],
  Montagem: [
    'Mecânica está OK?',
    'Pintura está OK?',
    'Conferir peças Orçamento',
    'Peças removidas: tirar foto antes de sucatear',
    'Passou pelo Controle Qualidade?',
  ],
  Polimento: ['Montagem está OK?', 'Conferir peças Orçamento', 'Passou pelo Controle Qualidade?'],
  'Lavagem / Higienização': [
    'Polimento está OK?',
    'Conferir peças Orçamento',
    'Passou pelo Controle Qualidade?',
  ],
  Acabamento: ['Lavagem está OK?', 'Conferir peças Orçamento', 'Passou pelo Controle Qualidade?'],
  'Controle Qualidade Final': ['Utilizar Check-list', 'Finalizar e anexar ao processo'],
  'Fotos / Vistoria Seguradora': [
    'Fotos antes/depois (evidências)',
    'Organizar documentos do processo',
    'Enviar material para seguradora',
    'Registrar retorno/aprovação',
    'Arquivar vistoria',
  ],
  Finalizado: [
    'Check final do veículo',
    'Higienização final',
    'Cliente notificado e entrega agendada',
    'Confirmação de entrega / assinatura',
    'Encerrar atendimento',
  ],
};

interface ComentarioItem {
  data: Date;
  usuario: string;
  comentario: string;
}

interface PecaCompra {
  codigo: string;
  descricao: string;
  quantidade: number;
  quantidadeCompra: number;
  dataSolicitacao: Date;
  dataPrevista: Date;
  status: 'Aguardando' | 'Recebida' | 'Atrasada';
  comentario?: string;
}

const daysAgo = (days: number) => new Date(Date.now() - days * 24 * 60 * 60 * 1000);
const daysFromNow = (days: number) => new Date(Date.now() + days * 24 * 60 * 60 * 1000);

@Component({
  selector: 'app-workflow-detail',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    HistoryTableComponent,
    UploadListComponent,
    CommunicationListComponent,
  ],
  templateUrl: './workflow-detail.component.html',
  styleUrl: './workflow-detail.component.scss',
})
export class WorkflowDetailComponent implements OnInit, OnDestroy {
  workflow?: WorkflowDetalhe;
  activeTab: 'status' | 'historico' | 'anexos' | 'comunicacao' | 'comentarios' = 'status';
  observacaoTexto = '';
  readonly stepDefinitions = WORKFLOW_STEP_DEFINITIONS;
  selectedStepTipo: WorkflowEtapaTipo = WORKFLOW_STEP_DEFINITIONS[0].tipo;
  selectedStepLabel = WORKFLOW_STEP_DEFINITIONS[0].label;
  selectedChecklistItems: string[] = [];
  selectedChecklistState: boolean[] = [];
  checklistStateByStep: Record<string, boolean[]> = {};
  commentsByStep: Record<string, string> = {};
  filesByStep: Record<string, File[]> = {};
  readonly pecasCompra: PecaCompra[] = [
    {
      codigo: 'PAR-001',
      descricao: 'Parachoque dianteiro',
      quantidade: 1,
      quantidadeCompra: 1,
      dataSolicitacao: daysAgo(10),
      dataPrevista: daysAgo(2),
      status: 'Atrasada',
    },
    {
      codigo: 'FAR-014',
      descricao: 'Farol direito',
      quantidade: 1,
      quantidadeCompra: 1,
      dataSolicitacao: daysAgo(8),
      dataPrevista: daysAgo(1),
      status: 'Atrasada',
    },
    {
      codigo: 'CAP-110',
      descricao: 'Capô',
      quantidade: 1,
      quantidadeCompra: 1,
      dataSolicitacao: daysAgo(7),
      dataPrevista: daysFromNow(2),
      status: 'Recebida',
    },
    {
      codigo: 'LAM-220',
      descricao: 'Paralama esquerdo',
      quantidade: 1,
      quantidadeCompra: 1,
      dataSolicitacao: daysAgo(6),
      dataPrevista: daysFromNow(3),
      status: 'Recebida',
    },
    {
      codigo: 'GRA-310',
      descricao: 'Grade frontal',
      quantidade: 1,
      quantidadeCompra: 1,
      dataSolicitacao: daysAgo(5),
      dataPrevista: daysFromNow(4),
      status: 'Recebida',
    },
  ];
  isPecaComentarioModalOpen = false;
  pecaSelecionada: PecaCompra | null = null;
  pecaComentarioTexto = '';
  private etapaMap = new Map<WorkflowEtapaTipo, WorkflowEtapa>();
  private doneOverrides = new Set<WorkflowEtapaTipo>();
  private realFimOverrides = new Map<WorkflowEtapaTipo, Date>();
  private currentIndexOverride: number | null = null;
  private plannedDatesByStep: Partial<Record<WorkflowEtapaTipo, { inicio?: Date; fim?: Date }>> =
    {};
  comentarios: ComentarioItem[] = [
    {
      data: daysAgo(1),
      usuario: 'Carlos',
      comentario: 'Cliente pediu atualização sobre a etapa atual.',
    },
    {
      data: daysAgo(2),
      usuario: 'Juliana',
      comentario: 'Peças chegaram e j— foram conferidas.',
    },
    {
      data: daysAgo(3),
      usuario: 'Marcos',
      comentario: 'Equipe iniciou o processo de desmontagem conforme planejamento.',
    },
    {
      data: daysAgo(4),
      usuario: 'Aline',
      comentario: 'Seguradora solicitou fotos adicionais do reparo.',
    },
    {
      data: daysAgo(5),
      usuario: 'Rafael',
      comentario: 'Cliente confirmou a previsão de entrega.',
    },
  ];

  private subscription = new Subscription();

  constructor(
    private route: ActivatedRoute,
    private workflowsService: WorkflowsService,
  ) {}

  ngOnInit(): void {
    this.subscription.add(
      this.route.paramMap
        .pipe(switchMap((params) => this.workflowsService.getById(Number(params.get('id')))))
        .subscribe((workflow) => {
          this.workflow = workflow;
          this.etapaMap = new Map(workflow?.etapas?.map((etapa) => [etapa.tipo, etapa]) ?? []);
          this.currentIndexOverride = null;
          const defaultTipo = workflow?.etapaAtual ?? WORKFLOW_STEP_DEFINITIONS[0].tipo;
          this.selectStep(defaultTipo);
        }),
    );
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  get etapaAtual(): WorkflowEtapa | undefined {
    if (!this.workflow) {
      return undefined;
    }
    return this.workflow.etapas.find((etapa) => etapa.tipo === this.workflow?.etapaAtual);
  }

  getDataInicio(workflow: WorkflowDetalhe): Date | null {
    if (!workflow.criadoEm) {
      return null;
    }
    return workflow.criadoEm instanceof Date ? workflow.criadoEm : new Date(workflow.criadoEm);
  }

  getDataFinalPrevista(workflow: WorkflowDetalhe): Date | null {
    const dates: Date[] = [];
    if (workflow.dataPrevistaEntrega) {
      dates.push(
        workflow.dataPrevistaEntrega instanceof Date
          ? workflow.dataPrevistaEntrega
          : new Date(workflow.dataPrevistaEntrega),
      );
    }
    workflow.etapas.forEach((etapa) => {
      if (etapa.inicio) {
        dates.push(etapa.inicio instanceof Date ? etapa.inicio : new Date(etapa.inicio));
      }
      if (etapa.fim) {
        dates.push(etapa.fim instanceof Date ? etapa.fim : new Date(etapa.fim));
      }
    });
    workflow.historico.forEach((item) => {
      if (item.dataHora) {
        dates.push(item.dataHora instanceof Date ? item.dataHora : new Date(item.dataHora));
      }
    });
    if (!dates.length) {
      return null;
    }
    return new Date(Math.max(...dates.map((date) => date.getTime())));
  }

  getOsNumero(workflow: WorkflowDetalhe): string {
    const candidate =
      (workflow as WorkflowDetalhe & { numeroOs?: string | number }).numeroOs ??
      (workflow as WorkflowDetalhe & { osNumero?: string | number }).osNumero ??
      (workflow as WorkflowDetalhe & { codigoOs?: string | number }).codigoOs ??
      (workflow as WorkflowDetalhe & { numeroOS?: string | number }).numeroOS;
    if (candidate !== undefined && candidate !== null && String(candidate).trim()) {
      return String(candidate);
    }
    return `OS-${String(workflow.id).padStart(6, '0')}`;
  }

  get anexosTodos(): Anexo[] {
    if (!this.workflow) {
      return [];
    }
    return this.workflow.etapas.flatMap((etapa) => etapa.anexos);
  }

  get etapaAtualIndex(): number {
    if (!this.workflow) {
      return -1;
    }
    return this.workflow.etapas.findIndex((etapa) => etapa.tipo === this.workflow?.etapaAtual);
  }

  get effectiveCurrentIndex(): number {
    if (this.currentIndexOverride !== null) {
      return this.currentIndexOverride;
    }
    return this.etapaAtualIndex;
  }

  get selectedEtapa(): WorkflowEtapa | undefined {
    return this.etapaMap.get(this.selectedStepTipo);
  }

  get selectedPlannedInicio(): Date | null {
    return (
      this.selectedEtapa?.previstaInicio ??
      this.plannedDatesByStep[this.selectedStepTipo]?.inicio ??
      null
    );
  }

  get selectedPlannedFim(): Date | null {
    return (
      this.selectedEtapa?.previstaFim ?? this.plannedDatesByStep[this.selectedStepTipo]?.fim ?? null
    );
  }

  get selectedRealInicio(): Date | null {
    return this.selectedEtapa?.inicio ?? null;
  }

  get selectedRealFim(): Date | null {
    return this.realFimOverrides.get(this.selectedStepTipo) ?? this.selectedEtapa?.fim ?? null;
  }

  get selectedResponsavel(): string {
    return this.selectedEtapa?.responsavel || this.workflow?.responsavelAtual || '—';
  }

  get isCompraPecasSelected(): boolean {
    return this.selectedStepTipo === WorkflowEtapaTipo.COMPRA_PECAS;
  }

  get isSelectedLate(): boolean {
    return this.isStepLate(this.selectedStepTipo);
  }

  setTab(tab: 'status' | 'historico' | 'anexos' | 'comunicacao' | 'comentarios'): void {
    this.activeTab = tab;
  }

  selectStep(tipo: WorkflowEtapaTipo): void {
    this.selectedStepTipo = tipo;
    this.selectedStepLabel = this.getStepLabel(tipo);
    const items = CHECKLIST_POR_ETAPA[this.selectedStepLabel] ?? [];
    if (
      !this.checklistStateByStep[this.selectedStepLabel] ||
      this.checklistStateByStep[this.selectedStepLabel].length !== items.length
    ) {
      this.checklistStateByStep[this.selectedStepLabel] = items.map(() => false);
    }
    if (!this.commentsByStep[this.selectedStepLabel]) {
      this.commentsByStep[this.selectedStepLabel] = '';
    }
    if (!this.filesByStep[this.selectedStepLabel]) {
      this.filesByStep[this.selectedStepLabel] = [];
    }
    this.selectedChecklistItems = items;
    this.selectedChecklistState = this.checklistStateByStep[this.selectedStepLabel];
  }

  getStepLabel(tipo: WorkflowEtapaTipo): string {
    return WORKFLOW_STEP_DEFINITIONS.find((step) => step.tipo === tipo)?.label ?? 'Etapa';
  }

  getStepIndex(tipo: WorkflowEtapaTipo): number {
    return WORKFLOW_STEP_DEFINITIONS.findIndex((step) => step.tipo === tipo);
  }

  getEtapaFor(tipo: WorkflowEtapaTipo): WorkflowEtapa | undefined {
    return this.etapaMap.get(tipo);
  }

  isStepDone(index: number, tipo: WorkflowEtapaTipo): boolean {
    if (this.doneOverrides.has(tipo)) {
      return true;
    }
    return this.effectiveCurrentIndex !== -1 && index < this.effectiveCurrentIndex;
  }

  isStepCurrent(index: number): boolean {
    return this.effectiveCurrentIndex !== -1 && index === this.effectiveCurrentIndex;
  }

  isStepTodo(index: number, tipo: WorkflowEtapaTipo): boolean {
    return !this.isStepDone(index, tipo) && !this.isStepCurrent(index);
  }

  isStepLate(tipo: WorkflowEtapaTipo): boolean {
    const plannedFim = this.etapaMap.get(tipo)?.previstaFim ?? this.plannedDatesByStep[tipo]?.fim;
    const realFim = this.realFimOverrides.get(tipo) ?? this.etapaMap.get(tipo)?.fim;
    if (plannedFim && !realFim && new Date() > plannedFim) {
      return true;
    }
    const currentIndex = this.getStepIndex(tipo);
    return Boolean(
      this.workflow?.atrasoDias &&
      this.workflow.atrasoDias > 0 &&
      currentIndex === this.effectiveCurrentIndex,
    );
  }

  onFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = input.files ? Array.from(input.files) : [];
    this.filesByStep[this.selectedStepLabel] = files;
    input.value = '';
  }

  get selectedFiles(): File[] {
    return this.filesByStep[this.selectedStepLabel] ?? [];
  }

  markStepDone(): void {
    this.doneOverrides.add(this.selectedStepTipo);
    if (!this.realFimOverrides.has(this.selectedStepTipo) && !this.selectedEtapa?.fim) {
      this.realFimOverrides.set(this.selectedStepTipo, new Date());
    }
    const selectedIndex = this.getStepIndex(this.selectedStepTipo);
    if (selectedIndex === this.effectiveCurrentIndex) {
      const nextIndex = Math.min(selectedIndex + 1, WORKFLOW_STEP_DEFINITIONS.length - 1);
      this.currentIndexOverride = nextIndex;
      const nextTipo = WORKFLOW_STEP_DEFINITIONS[nextIndex]?.tipo;
      if (nextTipo) {
        this.selectStep(nextTipo);
      }
    }
  }

  avancarEtapa(): void {
    if (this.workflow) {
      this.workflowsService.avancarEtapa(this.workflow.id);
    }
  }

  adicionarObservacao(): void {
    if (!this.workflow || !this.etapaAtual) {
      return;
    }
    this.workflowsService.addObservacao(
      this.workflow.id,
      this.etapaAtual.tipo,
      this.observacaoTexto,
    );
    this.observacaoTexto = '';
  }

  toggleChecklist(itemId: number): void {
    if (!this.workflow || !this.etapaAtual) {
      return;
    }
    this.workflowsService.toggleChecklistItem(this.workflow.id, this.etapaAtual.tipo, itemId);
  }

  addChecklist(texto: string): void {
    if (!this.workflow || !this.etapaAtual) {
      return;
    }
    this.workflowsService.addChecklistItem(this.workflow.id, this.etapaAtual.tipo, texto);
  }

  getDiasDesde(data: Date): number {
    const diff = Date.now() - data.getTime();
    return Math.floor(diff / (24 * 60 * 60 * 1000));
  }

  openPecaComentario(peca: PecaCompra): void {
    this.pecaSelecionada = peca;
    this.pecaComentarioTexto = peca.comentario ?? '';
    this.isPecaComentarioModalOpen = true;
  }

  closePecaComentario(): void {
    this.isPecaComentarioModalOpen = false;
    this.pecaSelecionada = null;
    this.pecaComentarioTexto = '';
  }

  savePecaComentario(): void {
    if (this.pecaSelecionada) {
      this.pecaSelecionada.comentario = this.pecaComentarioTexto.trim();
    }
    this.closePecaComentario();
  }

  onFileSelected(event: Event): void {
    if (!this.workflow || !this.etapaAtual) {
      return;
    }
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file) {
      this.workflowsService.addAnexoMock(
        this.workflow.id,
        this.etapaAtual.tipo,
        file.name,
        'Equipe Oficina',
      );
      input.value = '';
    }
  }
}
